
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MasterLicenseLayout from '@/components/masterlicense/MasterLicenseLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Sparkles, Image, AlertCircle, Settings, Save, FilePen } from 'lucide-react';
import AIContentGenerator from '@/components/admin/marketing/AIContentGenerator';
import { useToast } from '@/hooks/use-toast';
import { BANNER_SIZES, BannerSize } from '@/types/banner';
import AIBannerSizeSelector from '@/components/admin/marketing/AIBannerSizeSelector';
import BannerPreview from '@/components/admin/marketing/BannerPreview';
import { useAIIntegration } from '@/hooks/useAIIntegration';
import { saveBannerDraft, loadBannerDraft, deleteBannerDraft } from '@/utils/banner/bannerCore';

const DRAFT_STORAGE_KEY = 'ai_banner_draft';

interface BannerDraft {
  selectedSize: BannerSize;
  bannerText: string;
  bannerImage: string | null;
  backgroundColor: string;
  isAnimated?: boolean;
  lastUpdated: number;
  templateId?: string;
  templateName?: string;
}

const MasterAIBannerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [selectedSize, setSelectedSize] = useState<BannerSize>('medium_rectangle');
  const [bannerText, setBannerText] = useState('');
  const [bannerImage, setBannerImage] = useState<string | null>(null);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [isAnimatedGif, setIsAnimatedGif] = useState(false);
  const { serviceStatus } = useAIIntegration();
  const [hasDraft, setHasDraft] = useState(false);
  const [isDraftSaved, setIsDraftSaved] = useState(true);
  const [fromTemplate, setFromTemplate] = useState<{id: string; name: string} | null>(null);

  const currentSizeConfig = BANNER_SIZES.find(size => size.value === selectedSize) || BANNER_SIZES[0];
  const pixelSize = currentSizeConfig?.width * currentSizeConfig?.height || 300 * 250;
  
  // Check if we came from a template
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const templateId = searchParams.get('template');
    const templateName = searchParams.get('name');
    
    if (templateId && templateName) {
      setFromTemplate({id: templateId, name: templateName});
      toast({
        title: "Template loaded",
        description: `${templateName} template is ready for editing`
      });
    }
  }, [location.search, toast]);
  
  // Load any existing draft on component mount
  useEffect(() => {
    const savedDraft = loadBannerDraft<BannerDraft>(DRAFT_STORAGE_KEY);
    if (savedDraft) {
      try {
        setSelectedSize(savedDraft.selectedSize);
        setBannerText(savedDraft.bannerText);
        setBannerImage(savedDraft.bannerImage);
        setBackgroundColor(savedDraft.backgroundColor);
        if (savedDraft.isAnimated !== undefined) {
          setIsAnimatedGif(savedDraft.isAnimated);
        }
        setHasDraft(true);
        setIsDraftSaved(true);
        
        // If from template, show template info
        if (savedDraft.templateId && savedDraft.templateName) {
          setFromTemplate({
            id: savedDraft.templateId,
            name: savedDraft.templateName
          });
        }
        
        // Show toast notification about loaded draft
        toast({
          title: "Draft loaded",
          description: `Loaded your saved draft from ${new Date(savedDraft.lastUpdated).toLocaleString()}`,
        });
      } catch (e) {
        console.error("Error loading draft:", e);
      }
    }
  }, [toast]);

  // Track changes to mark draft as unsaved
  useEffect(() => {
    if (hasDraft) {
      setIsDraftSaved(false);
    }
  }, [bannerText, bannerImage, selectedSize, backgroundColor]);

  // Auto-save draft when content changes (with debounce)
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (!isDraftSaved && (bannerText || bannerImage)) {
        saveDraft();
      }
    }, 3000); // Auto-save after 3 seconds of inactivity

    return () => clearTimeout(autoSaveTimer);
  }, [bannerText, bannerImage, selectedSize, backgroundColor, isDraftSaved]);
  
  const saveDraft = () => {
    // Only save if there's content
    if (!bannerText && !bannerImage) {
      return;
    }
    
    const draft: BannerDraft = {
      selectedSize,
      bannerText,
      bannerImage,
      backgroundColor,
      isAnimated: isAnimatedGif,
      lastUpdated: Date.now(),
      ...(fromTemplate ? {
        templateId: fromTemplate.id,
        templateName: fromTemplate.name
      } : {})
    };
    
    saveBannerDraft(DRAFT_STORAGE_KEY, draft);
    setIsDraftSaved(true);
    setHasDraft(true);
    
    toast({
      title: "Draft saved",
      description: "Your banner draft has been saved"
    });
  };
  
  const deleteDraft = () => {
    deleteBannerDraft(DRAFT_STORAGE_KEY);
    setHasDraft(false);
    setIsDraftSaved(true);
    
    // Reset to default values
    setSelectedSize('medium_rectangle');
    setBannerText('');
    setBannerImage(null);
    setBackgroundColor('#ffffff');
    setIsAnimatedGif(false);
    setFromTemplate(null);
    
    toast({
      title: "Draft deleted",
      description: "Your banner draft has been discarded"
    });
  };

  const handleSaveBanner = () => {
    if (!bannerText && !bannerImage) {
      toast({
        title: "Banner content required",
        description: "Please generate and select banner text or image before saving.",
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Banner saved",
      description: "Your AI-generated banner has been saved successfully."
    });
    
    // In a real implementation, this would save the banner to a database
    // After saving, clear the draft
    deleteBannerDraft(DRAFT_STORAGE_KEY);
    setHasDraft(false);
    setIsDraftSaved(true);
    
    navigate('/master/marketing/manage-banners');
  };

  // Mock logout function for MasterLicenseLayout
  const handleLogout = () => {
    console.log("Logout action would happen here");
    // In a real implementation, this would handle the logout process
  };

  return (
    <MasterLicenseLayout activeTab="ai-banner" onLogout={handleLogout}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Banner Generator</h1>
            <p className="text-gray-600">Create marketing banners with AI-generated content</p>
          </div>
          <div className="mt-4 md:mt-0 space-x-2">
            <Button
              variant="outline"
              onClick={() => navigate('/master/marketing/banner-creator')}
            >
              Switch to Manual Creator
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/master/marketing/banner-templates')}
            >
              Browse Templates
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/master/marketing/manage-banners')}
            >
              View Banner Library
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/master/settings', { state: { activeTab: 'ai' } })}
            >
              <Settings className="h-4 w-4 mr-2" />
              AI Settings
            </Button>
          </div>
        </div>

        {!serviceStatus.anyConnected && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>AI services not connected</AlertTitle>
            <AlertDescription>
              You need to connect at least one AI service in the settings to use the AI banner generator.
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-4"
                onClick={() => navigate('/master/settings', { state: { activeTab: 'ai' } })}
              >
                Go to AI Settings
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Draft Status Bar */}
        {(hasDraft || !isDraftSaved) && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-6 flex justify-between items-center">
            <div className="flex items-center">
              <FilePen className="h-5 w-5 text-amber-600 mr-2" />
              <div className="flex flex-col">
                <span className="text-amber-700">
                  {isDraftSaved 
                    ? "Draft loaded" 
                    : "Unsaved changes (auto-saving...)"
                  }
                </span>
                {fromTemplate && (
                  <span className="text-xs text-amber-600">Based on template: {fromTemplate.name}</span>
                )}
              </div>
            </div>
            <div className="space-x-2">
              <Button 
                variant="outline"
                size="sm"
                onClick={saveDraft} 
                disabled={isDraftSaved || (!bannerText && !bannerImage)} 
                className="text-amber-700 border-amber-300 hover:bg-amber-100"
              >
                <Save className="h-4 w-4 mr-1" />
                Save Draft
              </Button>
              <Button 
                variant="outline"
                size="sm"
                onClick={deleteDraft} 
                className="text-red-700 border-red-300 hover:bg-red-100"
              >
                Discard Draft
              </Button>
            </div>
          </div>
        )}

        {/* Animated GIF Banner */}
        {isAnimatedGif && bannerImage && (
          <Alert className="mb-6 bg-blue-50 border-blue-200">
            <Image className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-800">Animated GIF Banner</AlertTitle>
            <AlertDescription className="text-blue-700">
              You are working with an animated GIF banner. Certain AI-generated elements may not be applied to animated content.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Preview Panel - On the left */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Preview</CardTitle>
                <CardDescription>
                  {currentSizeConfig.name} ({currentSizeConfig.width}x{currentSizeConfig.height})
                  {isAnimatedGif && <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700">Animated</Badge>}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <BannerPreview 
                  size={selectedSize}
                  text={bannerText}
                  imageUrl={bannerImage}
                  backgroundColor={backgroundColor}
                  textColor="#000000"
                />
                
                {!bannerText && !bannerImage && (
                  <div className="flex flex-col items-center justify-center text-center p-4 mt-4 border border-dashed border-gray-300 rounded-md">
                    <Image className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-gray-500 text-sm">
                      Generate content to see your banner preview
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Content Generator Panel */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <Sparkles className="h-5 w-5 mr-2 text-primary" />
                      AI Content Generator
                    </CardTitle>
                    <CardDescription>Generate banner text and images with AI</CardDescription>
                  </div>
                  {serviceStatus.anyConnected && (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {serviceStatus.openAI ? "OpenAI" : 
                       serviceStatus.midjourney ? "Midjourney" : 
                       serviceStatus.stabilityAI ? "Stability AI" : 
                       serviceStatus.huggingFace ? "Hugging Face" : ""}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <AIContentGenerator 
                  bannerSize={pixelSize} 
                  onContentGenerated={setBannerText}
                  onImageGenerated={setBannerImage}
                  setIsAnimatedGif={setIsAnimatedGif}
                />
              </CardContent>
            </Card>
            
            {/* Banner Size Selector - Now on the right */}
            <Card>
              <CardHeader>
                <CardTitle>Banner Size</CardTitle>
                <CardDescription>Select a standard banner size for your campaign</CardDescription>
              </CardHeader>
              <CardContent>
                <AIBannerSizeSelector 
                  selectedSize={selectedSize}
                  onSizeChange={(size) => {
                    setSelectedSize(size);
                    // Mark as unsaved when size changes
                    if (bannerText || bannerImage) {
                      setIsDraftSaved(false);
                    }
                  }}
                  showAllSizes={true}
                />
              </CardContent>
            </Card>

            <div className="flex justify-end space-x-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  // Check for unsaved changes
                  if (!isDraftSaved && (bannerText || bannerImage)) {
                    if (confirm("You have unsaved changes. Do you want to save your draft before leaving?")) {
                      saveDraft();
                    }
                  }
                  navigate('/master/marketing/manage-banners');
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveBanner}
                disabled={(!bannerText && !bannerImage) || !serviceStatus.anyConnected}
              >
                Save Banner
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MasterLicenseLayout>
  );
};

export default MasterAIBannerPage;
